import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { Button, Dropdown, Form, Menu, message, Modal, Tabs } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { useMediaQuery } from 'react-responsive'
import { useParams } from 'react-router-dom/cjs/react-router-dom'
import { blue } from '../../../color'
import AccessControlAction from '../../../components/AccessControlAction'
import { EmptyText, TitleContentBox } from '../../../components/Common/CellText'
import FileItem from '../../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import PDFViewer from '../../../components/PDFViewer/PDFViewer'
import SpinnerInlineComponent from '../../../components/SpinnerInlineComponent'
import { ACL_ACTION_TYPE, DATE_FORMAT_DEFAULT } from '../../../constants'
import DashboardLayout from '../../../layouts/DashboardLayout'
import utils from '../../../utils'
import ListHistoryBoardReportModal from '../../ListHistoryLog/ListHistoryBoardReportModal'
import BoardReportComment from '../BoardReportComment'
import BoardReportUpdateModal from '../BoardReportUpdate/BoardReportUpdateModal'
import {
  BlockLeft,
  BlockRight,
  DocumentContentWrapper,
  DocumentInfoList,
  HeadingButtonWrapper,
  TextRich,
} from './BoardReportDetailPageStyled'
import {
  ArrowLeftBroken,
  HistoryOutlineIcon,
  MenuDotsBoldIcon,
} from '../../../components/AppIcon'
import { BackButton } from '../../../components/PageTitle/PageTitleStyled'
import AppPageHeader from '../../../components/AppPageHeader'

const { confirm } = Modal

const BoardReportDetailPage = props => {
  const [form] = Form.useForm()

  const isTabletOrMobile = useMediaQuery({
    query: '(max-width: 1299px)',
  })

  const {
    match,
    listHistoryLogStore,
    boardReportStore,
    loadingAnimationStore,
    history,
    aclStore,
  } = props

  const { documentId } = match.params
  const { aclActionsByUser } = aclStore

  const { selectedBoardReport, boardReportCommentList } = boardReportStore

  const {
    title,
    content,
    created_at,
    user_created,
    files,
  } = selectedBoardReport

  const [isLoadingComment, setIsLoadingComment] = useState(false)
  const [attachmentForView, setAttachmentForView] = useState()
  const { code } = useParams()
  const [isVisibleUpdateModal, setIsVisibleUpdateModal] = useState(false)
  const [isVisibleHistoryModal, setIsVisibleHistoryModal] = useState(false)

  const formatName = useCallback(name => {
    if (!name || typeof name !== 'string') return ''
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }, [])

  const handleDeleteBoardReport = useCallback(async () => {
    loadingAnimationStore.showSpinner(true)
    try {
      await boardReportStore.deleteBoardReportByCode(code)
      history.push('/board-report')
      message.success('Xóa báo cáo thành công')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [code, boardReportStore, loadingAnimationStore, history])

  const showConfirm = useCallback(() => {
    confirm({
      title: 'Bạn có muốn xóa báo cáo này không?',
      icon: <QuestionCircleOutlined style={{ color: 'red' }} />,
      okText: 'Đồng ý',
      cancelText: 'Không',
      okType: 'danger',
      onOk() {
        handleDeleteBoardReport()
      },
    })
  }, [handleDeleteBoardReport])

  const handleOpenUpdateModal = useCallback(() => {
    setIsVisibleUpdateModal(true)
  }, [])

  const handleCloseUpdateModal = useCallback(() => {
    setIsVisibleUpdateModal(false)
  }, [])

  const handleUpdateSuccess = useCallback(async () => {
    await boardReportStore.getBoardReportByCode(code)
    setIsVisibleUpdateModal(false)
  }, [code, boardReportStore])

  const handleCloseHistoryModal = useCallback(() => {
    setIsVisibleHistoryModal(false)
  }, [])

  const menuHandleDocument = useMemo(
    () => (
      <Menu>
        <AccessControlAction
          aclActionType={ACL_ACTION_TYPE.update__REPORT_HDQT}>
          <Menu.Item
            icon={<EditOutlined style={{ color: blue }} />}
            onClick={handleOpenUpdateModal}>
            <span style={{ color: blue }}>Chỉnh sửa báo cáo</span>
          </Menu.Item>
        </AccessControlAction>
        {/* <AccessControlAction check={true}>
          <Menu.Item
            icon={<HistoryOutlineIcon style={{ color: blue }} />}
            onClick={() => setIsVisibleHistoryModal(true)}>
            <span style={{ color: blue }}>Lịch sử</span>
          </Menu.Item>
        </AccessControlAction> */}

        <AccessControlAction
          aclActionType={ACL_ACTION_TYPE.delete__REPORT_HDQT}>
          <Menu.Item
            danger
            onClick={showConfirm}
            icon={<DeleteOutlined />}
            key="2">
            Xóa báo cáo
          </Menu.Item>
        </AccessControlAction>
      </Menu>
    ),
    [handleOpenUpdateModal, showConfirm]
  )

  const renderAttachment = useMemo(
    () => (
      <PDFViewer
        fileId={attachmentForView?.fileId}
        customStyleObj={{ height: isTabletOrMobile ? 500 : 1000 }}
        fileType={utils.getExtensionFile(attachmentForView?.fileName)}
      />
    ),
    [attachmentForView, isTabletOrMobile]
  )

  const renderFiles = useMemo(() => {
    if (!files) {
      return <EmptyText>Không có tài liệu đính kèm</EmptyText>
    }

    return files.map(file => (
      <FileItem
        key={file.id}
        file_id={file.fileId}
        file_name={file.fileName}
        file_type={utils.getExtensionFile(file.fileName)}
      />
    ))
  }, [files])

  const formattedCreatedAt = useMemo(() => {
    if (!created_at) {
      return <EmptyText>Không có ngày tạo</EmptyText>
    }

    return (
      <>
        <CalendarOutlined style={{ color: blue, marginRight: 4 }} />
        <span>{moment(created_at).format(DATE_FORMAT_DEFAULT)}</span>
      </>
    )
  }, [created_at])

  const formattedUserCreated = useMemo(() => {
    return (
      formatName(user_created?.name_lowercase) || (
        <EmptyText>Không có người tạo</EmptyText>
      )
    )
  }, [user_created, formatName])

  const renderContent = useMemo(() => {
    if (!content) {
      return <EmptyText>Không có nội dung</EmptyText>
    }

    return (
      <TextRich
        className="html-content"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )
  }, [content])

  const fileMap = useMemo(() => {
    return (
      boardReportStore.selectedBoardReport?.files?.reduce((acc, file) => {
        acc[file.fileId] = file.fileName
        return acc
      }, {}) || {}
    )
  }, [boardReportStore.selectedBoardReport?.files])

  const renderDocumentInfo = useMemo(
    () => (
      <div className="custom-create-page-content-wrapper">
        <h3 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600 }}>
          Thông tin chung
        </h3>
        <DocumentInfoList>
          <dt>Tiêu đề</dt>
          <dd>{title}</dd>
          <dt>Nội dung</dt>
          <dd>{renderContent}</dd>
          <dt>Ngày tạo</dt>
          <dd>{formattedCreatedAt}</dd>
          <dt>Người tạo</dt>
          <dd>{formattedUserCreated}</dd>
          <dt>Tài liệu đính kèm</dt>
          <dd>
            <div>{renderFiles}</div>
          </dd>
        </DocumentInfoList>
      </div>
    ),
    [
      title,
      renderContent,
      formattedCreatedAt,
      formattedUserCreated,
      renderFiles,
    ]
  )

  const renderCommentBlock = useMemo(
    () => (
      <div className="custom-create-page-content-wrapper">
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Ý kiến" key="1">
            <BoardReportComment setIsLoadingComment={setIsLoadingComment} />
            <SpinnerInlineComponent
              sizeSpin="small"
              isLoading={isLoadingComment}
            />
          </Tabs.TabPane>
        </Tabs>
      </div>
    ),
    [isLoadingComment]
  )

  useEffect(() => {
    if (!files) return
    setAttachmentForView(
      files.find(file =>
        ['pdf', 'doc', 'docx'].includes(utils.getExtensionFile(file.fileName))
      )
    )
  }, [files])

  useEffect(() => {
    ;(async () => {
      loadingAnimationStore.showSpinner(true)
      try {
        await boardReportStore.getBoardReportByCode(code)
      } catch (err) {
        history.push('/board-report')
        message.error(err?.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    })()

    return () => {
      form.resetFields()
      boardReportStore.clearSelectedBoardReport()
    }
  }, [code, boardReportStore, loadingAnimationStore, history, form])

  return (
    <DashboardLayout>
      <Helmet>
        <title>{`${title} | VIMC Quản lý VB nội bộ`}</title>
      </Helmet>
      <AppPageHeader
        title="Báo cáo HĐQT"
        showBackButton
        addon={
          <Dropdown overlay={menuHandleDocument} trigger={['click']}>
            <Button
              style={{ width: 40, height: 40, flexShrink: 0 }}
              icon={
                <MenuDotsBoldIcon
                  style={{ width: 20, height: 20, transform: 'rotate(90deg)' }}
                />
              }
              className="custom-ant-button icon-btn"
              type="default"
            />
          </Dropdown>
        }
      />
      <DocumentContentWrapper>
        <BlockLeft>{renderAttachment}</BlockLeft>
        <BlockRight>
          {renderDocumentInfo}
          <div style={{ marginBottom: 24 }} />
          {renderCommentBlock}
        </BlockRight>
      </DocumentContentWrapper>
      <BoardReportUpdateModal
        visible={isVisibleUpdateModal}
        selectedBoardReport={boardReportStore.selectedBoardReport}
        onCancel={handleCloseUpdateModal}
        onSuccess={handleUpdateSuccess}
      />
      <ListHistoryBoardReportModal
        isVisible={isVisibleHistoryModal}
        handleCloseHistory={handleCloseHistoryModal}
        taskInfo={{ code }}
        type="board_report"
        fileMap={fileMap}
      />
    </DashboardLayout>
  )
}

BoardReportDetailPage.propTypes = {}

export default inject(
  'commonStore',
  'internalDocumentStore',
  'loadingAnimationStore',
  'authenticationStore',
  'departmentStore',
  'staffStore',
  'fileStore',
  'notificationStore',
  'selectPeopleStore',
  'boardReportStore',
  'listHistoryLogStore',
  'aclStore'
)(observer(BoardReportDetailPage))
